import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { fmtHora as fmtTime, fmtDataHora as fmtFull } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ícone customizado de caminhão (SVG inline → DivIcon)
const truckIcon = L.divIcon({
  className: "rastreio-truck-icon",
  iconSize: [44, 44],
  iconAnchor: [22, 22],
  html: `<div style="background:#b8935a;border:3px solid #0d0d0f;border-radius:50%;width:44px;height:44px;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 16px rgba(0,0,0,0.5);">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="#0d0d0f" stroke="#0d0d0f" stroke-width="0.5">
      <path d="M3 17V7h11v10H3zm14 0v-5l3-3h2v8h-5zM7 21q-.825 0-1.413-.588T5 19q0-.825.588-1.413T7 17q.825 0 1.413.588T9 19q0 .825-.588 1.413T7 21zm12 0q-.825 0-1.413-.588T17 19q0-.825.588-1.413T19 17q.825 0 1.413.588T21 19q0 .825-.588 1.413T19 21z"/>
    </svg>
  </div>`,
});

// Auto-recenter quando posição muda
function MapAutoCenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

// Haversine para frontend (cálculo local de progresso)
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Ícone do destino (marcador final)
const destIcon = L.divIcon({
  className: "rastreio-dest-icon",
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  html: `<div style="background:#5fa86c;border:3px solid #0d0d0f;border-radius:50% 50% 50% 0;width:32px;height:32px;transform:rotate(-45deg);box-shadow:0 4px 14px rgba(0,0,0,0.5);">
    <div style="transform:rotate(45deg);display:flex;align-items:center;justify-content:center;height:100%;font-size:14px;">🏁</div>
  </div>`,
});

export default function RastreioPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  // Polling: a cada 15s se em_transito, para se entregue
  useEffect(() => {
    let cancel = false;
    let timer = null;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/rastreio/${token}`, { timeout: 10000 });
        if (cancel) return;
        setData(r.data);
        setLastFetch(new Date());
        setError(null);
        // re-agenda apenas se ainda em trânsito
        if (r.data?.status === "em_transito") {
          timer = setTimeout(fetchOnce, 15000);
        }
      } catch (e) {
        if (cancel) return;
        if (e.response?.status === 404) {
          setError("Rastreamento não encontrado ou já expirou.");
        } else {
          setError("Erro de conexão. Tentando novamente…");
          timer = setTimeout(fetchOnce, 10000);
        }
      }
    };
    fetchOnce();
    return () => { cancel = true; if (timer) clearTimeout(timer); };
  }, [token]);

  const coords = useMemo(
    () => (data?.coordenadas || []).map(c => [c.lat, c.lng]),
    [data?.coordenadas]
  );
  const center = useMemo(() => {
    const last = data?.ultima_posicao;
    if (last?.lat && last?.lng) return [last.lat, last.lng];
    if (coords.length) return coords[coords.length - 1];
    if (data?.destino?.lat && data?.destino?.lng) return [data.destino.lat, data.destino.lng];
    return [-23.5505, -46.6333]; // SP fallback
  }, [data, coords]);

  // Barra de progresso baseada em status
  const progressPct = useMemo(() => {
    if (!data) return 0;
    if (data.status === "entregue") return 100;
    if (data.status === "em_transito") {
      // se tem ETA, usa distance percorrida proporcional
      if (data.eta?.distance_km != null && data.coordenadas?.length > 1) {
        const first = data.coordenadas[0];
        const last = data.coordenadas[data.coordenadas.length - 1];
        if (first && last && data.destino?.lat) {
          // distância total saída→destino em linha reta (estimativa simples)
          const totalDist = haversineKm(first.lat, first.lng, data.destino.lat, data.destino.lng);
          const restante = data.eta.distance_km;
          if (totalDist > 0) {
            return Math.max(33, Math.min(95, Math.round(((totalDist - restante) / totalDist) * 100)));
          }
        }
      }
      return 50;
    }
    return 10; // pendente/saiu
  }, [data]);

  if (error) {
    return (
      <div data-testid="rastreio-error" style={styles.container}>
        <div style={styles.errorBox}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <div style={{ fontSize: 18, color: "#f5f0e8" }}>{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>
          <div style={{ fontSize: 14, color: "#8a857a", letterSpacing: 1 }}>CARREGANDO RASTREAMENTO…</div>
        </div>
      </div>
    );
  }

  const isEntregue = data.status === "entregue";

  return (
    <div data-testid="rastreio-page" style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontFamily: "'Fraunces',serif", color: "#b8935a", fontSize: 22, letterSpacing: 2 }}>CAMASA</div>
          <div style={{ width: 1, height: 22, background: "#2b2833" }}></div>
          <div style={{ color: "#cdc8be", fontSize: 13, letterSpacing: 1, textTransform: "uppercase" }}>
            Rastreamento de Entrega
          </div>
        </div>
        <div style={styles.statusBadge(isEntregue)}>
          {isEntregue ? "✅ ENTREGUE" : "🚚 EM TRÂNSITO"}
        </div>
      </header>

      {/* Info card */}
      <div style={styles.infoCard} data-testid="rastreio-info">
        <div>
          <div style={styles.label}>Obra</div>
          <div style={styles.value}>{data.obra_nome || "—"}</div>
        </div>
        <div>
          <div style={styles.label}>Saída</div>
          <div style={styles.value}>{fmtFull(data.saida_em)}</div>
        </div>
        <div>
          <div style={styles.label}>Frentista</div>
          <div style={styles.value}>{data.frentista_nome || "—"}</div>
        </div>
        <div>
          <div style={styles.label}>{isEntregue ? "Entregue às" : "ETA"}</div>
          <div style={styles.value} data-testid="rastreio-eta">
            {isEntregue
              ? fmtFull(data.chegada_em)
              : (data.eta?.eta_minutos != null
                  ? `~${data.eta.eta_minutos} min · ${data.eta.distance_km} km`
                  : "Calculando…")}
          </div>
        </div>
      </div>

      {/* Barra de progresso Saiu → Em trânsito → Entregue */}
      <div style={styles.progressWrap} data-testid="rastreio-progress">
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: `${progressPct}%` }} />
        </div>
        <div style={styles.progressSteps}>
          <div style={{ ...styles.progressStep, ...(progressPct >= 10 ? styles.progressStepDone : {}) }}>
            <div style={styles.progressDot}>{progressPct >= 10 ? "✓" : "1"}</div>
            <span>Saída</span>
          </div>
          <div style={{ ...styles.progressStep, ...(progressPct >= 50 ? styles.progressStepDone : {}) }}>
            <div style={styles.progressDot}>{progressPct >= 50 ? "✓" : "2"}</div>
            <span>Em trânsito</span>
          </div>
          <div style={{ ...styles.progressStep, ...(progressPct >= 100 ? styles.progressStepDone : {}) }}>
            <div style={styles.progressDot}>{progressPct >= 100 ? "✓" : "3"}</div>
            <span>Entregue</span>
          </div>
        </div>
      </div>

      {/* Observação livre do frete (conteúdo descritivo) */}
      {data.observacao && (
        <div
          data-testid="rastreio-observacao"
          style={{
            background: "rgba(184,147,90,0.10)",
            border: "1px solid rgba(184,147,90,0.3)",
            borderLeft: "3px solid #b8935a",
            padding: "12px 16px",
            marginBottom: 12,
            borderRadius: 4,
            fontSize: 14,
            color: "#cdc8be",
            fontStyle: "italic",
          }}
        >
          📦 {data.observacao}
        </div>
      )}

      {/* Mensagem de entrega confirmada */}
      {isEntregue && (
        <div style={styles.deliveredBanner} data-testid="rastreio-delivered-banner">
          <div style={{ fontSize: 36 }}>✅</div>
          <div>
            <div style={{ fontSize: 18, color: "#5fa86c", fontWeight: 600 }}>Entrega Confirmada</div>
            <div style={{ fontSize: 13, color: "#cdc8be", marginTop: 4 }}>
              Recebida às {fmtTime(data.chegada_em)} de {new Date(data.chegada_em).toLocaleDateString("pt-BR")}
            </div>
          </div>
        </div>
      )}

      {/* Mapa */}
      <div style={styles.mapWrap} data-testid="rastreio-map">
        <MapContainer
          center={center}
          zoom={14}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {coords.length > 1 && (
            <Polyline positions={coords} pathOptions={{ color: "#b8935a", weight: 4, opacity: 0.85 }} />
          )}
          {data.destino?.lat != null && data.destino?.lng != null && (
            <Marker position={[data.destino.lat, data.destino.lng]} icon={destIcon}>
              <Popup>
                <div style={{ fontFamily: "system-ui", fontSize: 13 }}>
                  <strong>Destino</strong><br />
                  {data.endereco_entrega || "—"}
                </div>
              </Popup>
            </Marker>
          )}
          {data.ultima_posicao && (
            <Marker position={[data.ultima_posicao.lat, data.ultima_posicao.lng]} icon={truckIcon}>
              <Popup>
                <div style={{ fontFamily: "system-ui", fontSize: 13 }}>
                  <strong>{data.frentista_nome}</strong><br />
                  {isEntregue ? "Entregue" : "Em trânsito"}<br />
                  Atualizado: {fmtTime(data.ultima_posicao.ts)}
                </div>
              </Popup>
            </Marker>
          )}
          <MapAutoCenter center={center} />
        </MapContainer>
      </div>

      {/* Live indicator */}
      {!isEntregue && (
        <div style={styles.liveDot} data-testid="rastreio-live-indicator">
          <span style={styles.pulse}></span>
          <span style={{ fontSize: 11, color: "#8a857a", letterSpacing: 1 }}>AO VIVO · ATUALIZA A CADA 15s</span>
        </div>
      )}

      {/* Materiais */}
      {(data.materiais || []).length > 0 && (
        <div style={styles.matCard}>
          <div style={styles.label}>Materiais a entregar</div>
          <ul style={{ margin: "8px 0 0", padding: "0 0 0 18px", color: "#cdc8be", fontSize: 13, lineHeight: 1.7 }}>
            {data.materiais.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      <style>{`
        @keyframes pulse-rastreio {
          0% { transform: scale(1); opacity: 1; }
          70% { transform: scale(2.5); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background: "#0d0d0f",
    color: "#cdc8be",
    fontFamily: "'Helvetica Neue',Arial,sans-serif",
    padding: "16px",
    boxSizing: "border-box",
  },
  errorBox: {
    maxWidth: 480,
    margin: "80px auto",
    background: "#15141a",
    border: "1px solid #2b2833",
    padding: "40px 24px",
    textAlign: "center",
    borderRadius: 6,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    paddingBottom: 16,
    borderBottom: "1px solid #2b2833",
    marginBottom: 16,
  },
  statusBadge: (entregue) => ({
    background: entregue ? "rgba(95,168,108,0.15)" : "rgba(184,147,90,0.15)",
    color: entregue ? "#5fa86c" : "#b8935a",
    border: `1px solid ${entregue ? "#5fa86c" : "#b8935a"}`,
    padding: "6px 14px",
    borderRadius: 4,
    fontSize: 11,
    letterSpacing: 1.5,
    fontWeight: 600,
  }),
  infoCard: {
    background: "#15141a",
    border: "1px solid #2b2833",
    padding: 16,
    borderRadius: 6,
    marginBottom: 12,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 14,
  },
  label: {
    fontSize: 10,
    color: "#8a857a",
    letterSpacing: 1.5,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  value: { color: "#f5f0e8", fontSize: 14, fontWeight: 500 },
  deliveredBanner: {
    background: "rgba(95,168,108,0.08)",
    border: "1px solid rgba(95,168,108,0.3)",
    borderRadius: 6,
    padding: 16,
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  mapWrap: {
    height: "55vh",
    minHeight: 340,
    width: "100%",
    border: "1px solid #2b2833",
    borderRadius: 6,
    overflow: "hidden",
    position: "relative",
  },
  liveDot: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "12px 0",
    justifyContent: "center",
  },
  pulse: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#b8935a",
    boxShadow: "0 0 0 0 #b8935a",
    animation: "pulse-rastreio 1.6s infinite",
    display: "inline-block",
  },
  matCard: {
    background: "#15141a",
    border: "1px solid #2b2833",
    padding: 14,
    borderRadius: 6,
  },
  progressWrap: {
    background: "#15141a",
    border: "1px solid #2b2833",
    padding: 16,
    borderRadius: 6,
    marginBottom: 12,
  },
  progressBar: {
    height: 6,
    background: "rgba(184,147,90,0.1)",
    borderRadius: 3,
    overflow: "hidden",
    marginBottom: 14,
  },
  progressFill: {
    height: "100%",
    background: "linear-gradient(90deg, #b8935a 0%, #d4b27a 50%, #5fa86c 100%)",
    transition: "width 0.6s ease-out",
  },
  progressSteps: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 8,
  },
  progressStep: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    color: "#8a857a",
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  progressStepDone: {
    color: "#f5f0e8",
  },
  progressDot: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "#0d0d0f",
    border: "1.5px solid currentColor",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 600,
  },
};
